<template>
<v-card class="vms-surface" elevation="0">
    <v-toolbar flat class="px-4 pt-4 pb-2">

        <v-toolbar-title v-if="!showSearch"
            class="vms-page-title"
        >
        <span>Blacklist</span>

        </v-toolbar-title>

        <v-spacer></v-spacer>

        <VmsColumnVisibility
            v-model="selectedHeaders"
            :headers="headers"
            @change="toggleColumnVisibility"
            class="mr-3"
        />

        <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
                <v-hover v-slot="{ hover }">
                <v-btn 
                    :class="{ 'info--text' : hover }" 
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="addUser"
                >
                    <v-icon color="info">mdi-account-plus</v-icon>
                </v-btn>
                </v-hover>
            </template>
            <span class="text-caption">Add User</span>
        </v-tooltip>
    </v-toolbar>

    <VmsResponsiveList :items="facilityBlacklistWithIndex" loading-text="Loading blacklist…">
        <template #table>
            <v-data-table
                :headers="computedHeaders"
                :items="facilityBlacklistWithIndex"
                class="elevation-0"
            >
                <template v-slot:[`item.edit`]="{ item }">
                    <VmsIconAction
                        icon="mdi-pencil-outline"
                        label="Edit"
                        @click="editUser(item)"
                        :disabled="!!item.attributes?.removed_on"
                    />
                </template>

                <template v-slot:[`item.delete`]="{ item }">
                    <v-chip v-if="item.attributes?.removed_on"
                        color="success"
                    >
                        Removed
                    </v-chip>
                    <v-chip v-else
                        class="white--text"
                        color="red"
                        @click="deleteUser(item)"
                    >
                        Unblock
                    </v-chip>

                </template>

            </v-data-table>
        </template>

        <template #card="{ item }">
            <div class="vms-list-card__title">
                {{ item.attributes?.firstname }} {{ item.attributes?.lastname }}
            </div>
            <div class="vms-list-card__row">
                <span class="vms-list-card__label">Contact</span>
                <span>{{ item.attributes?.phone }}</span>
            </div>
            <div class="vms-list-card__row">
                <span class="vms-list-card__label">ID</span>
                <span>{{ item.attributes?.id_number }}</span>
            </div>
            <div class="vms-list-card__row">
                <span class="vms-list-card__label">From</span>
                <span>{{ item.attributes?.enforced_on }}</span>
            </div>
            <div class="vms-list-card__row">
                <span class="vms-list-card__label">To</span>
                <span>{{ item.attributes?.removed_on }}</span>
            </div>
            <div class="vms-list-card__actions">
                <VmsIconAction
                    icon="mdi-pencil-outline"
                    label="Edit"
                    @click="editUser(item)"
                    :disabled="!!item.attributes?.removed_on"
                />
                <v-chip v-if="item.attributes?.removed_on" color="success">
                    Removed
                </v-chip>
                <v-chip v-else class="white--text" color="red" @click="deleteUser(item)">
                    Unblock
                </v-chip>
            </div>
        </template>

        <template #empty>
            <div class="vms-empty">
                <v-icon large color="grey lighten-1">mdi-account-cancel</v-icon>
                <p class="mt-2 grey--text px-4">No blacklisted users</p>
            </div>
        </template>
    </VmsResponsiveList>


    <v-row justify="center">
        <v-dialog
            v-model="showUserModal"
            max-width="600"
            scrollable
        >
            <v-card>
                <v-card-title class="vms-form-title">
                    Blacklist
                </v-card-title>
                <v-card-subtitle class="vms-form-subtitle">
                    Block a visitor from checking in at this facility.
                </v-card-subtitle>

                <v-divider></v-divider>

                <v-card-text class="vms-form-card">
                    <v-form class="vms-form">
                        <div class="vms-form-section">
                            <span class="vms-form-section__label">Person</span>
                            <v-row>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        dense
                                        hide-details="auto"
                                        v-model="user.name"
                                        label="Full name"
                                        :error-messages="apiErrors.name"
                                        outlined
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <div class="vms-form-phone">
                                        <vue-phone-number-input
                                            v-model="phoneNumber"
                                            @update="updatePhoneNumber"
                                            :default-country-code="national_number"
                                            size="sm"
                                        ></vue-phone-number-input>
                                        <span class="text-caption red--text" v-if="apiErrors.phone">{{ apiErrors.phone[0] }}</span>
                                        <span class="text-caption red--text" v-else-if="phoneValidationError">{{ phoneValidationError }}</span>
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        dense
                                        hide-details="auto"
                                        v-model="user.id_number"
                                        label="National ID"
                                        :error-messages="apiErrors.id_number"
                                        outlined
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        dense
                                        hide-details="auto"
                                        :value="facilityName"
                                        label="Facility"
                                        readonly
                                        :error-messages="apiErrors.facility_id"
                                        outlined
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </div>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="vms-form-actions">

                    <v-btn
                        text
                        color="grey darken-1"
                        @click="closeModal"
                        class="text-capitalize"
                    >
                        Cancel
                    </v-btn>

                    <v-spacer></v-spacer>

                    <v-btn v-if="editUserModal"
                        color="primary"
                        class="text-capitalize white--text"
                        @click="saveUserUpdates"
                    >
                        Update
                    </v-btn>

                    <v-btn v-else
                        color="primary"
                        class="text-capitalize white--text"
                        @click="saveUser"
                    >
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-row>

</v-card>
</template>

<script>
import Blacklist from '../../models/Blacklist';
import FacilityBlacklistService from '../../services/facility_blacklist.service'
import VmsResponsiveList from '../../components/VmsResponsiveList.vue';
import VmsIconAction from '../../components/VmsIconAction.vue';
import VmsColumnVisibility from '../../components/VmsColumnVisibility.vue';
import columnVisibility from '../../mixins/columnVisibility';
export default {
    name : "Blacklist",
    components: { VmsResponsiveList, VmsIconAction, VmsColumnVisibility },
    mixins: [columnVisibility],
    props : [
        'facility'
    ],

    data () {
        return {
            user : new Blacklist(),
            facilityBlacklist : [],
            headers : [
                {
                    text: "#",
                    align: "start",
                    filterable: true,
                    value: "index",
                    visible: true,
                },{
                    text: "First Name",
                    align: "start",
                    filterable: true,
                    value: "attributes.firstname",
                    visible: true,
                },{
                    text: "Last Name",
                    align: "start",
                    filterable: true,
                    value: "attributes.lastname",
                    visible: true,
                },{
                    text: "Contact",
                    align: "start",
                    filterable: true,
                    value: "attributes.phone",
                    visible: false,
                },{
                    text: "ID",
                    align: "start",
                    filterable: true,
                    value: "attributes.id_number",
                    visible: true,
                },{
                    text: "From Date",
                    align: "start",
                    filterable: true,
                    value: "attributes.enforced_on",
                    visible: true,
                },{
                    text: "To Date",
                    align: "start",
                    filterable: true,
                    value: "attributes.removed_on",
                    visible: false,
                },{
                    text: "Remove",
                    align: "center",
                    filterable: false,
                    value : "delete",
                    visible: true,
                    sticky: true,
                }
            ],
            showUserModal : false,
            roles : [],
            apiErrors : [],
            showSearch : null,
            editUserModal : false,
            assignRoleModal : false,
            national_number : "KE",
            phoneNumber : null,
            phoneValidationError : null,
            phoneErrorColor : "blue",
            
        }
    },

    created() {
        this.initColumnVisibility();
        this.$nextTick(() => {
            this.getBlacklist();
        });
    },

    computed : {

        facilityName() {
            return this.facility?.attributes?.facility
                || this.facility?.facility
                || '';
        },

        facilityBlacklistWithIndex() {
            return this.facilityBlacklist.map(
                (facilityBlacklist, index) => ({
                    ...facilityBlacklist,
                    index: index + 1
                })
            )
        }

    },

    methods : {

        getBlacklist()
        {
            FacilityBlacklistService.facilityBlacklist(this.facility.id).then(
                success => {
                    
                    this.facilityBlacklist = success.data ? success.data.data : this.facilityBlacklist
                    
                },
                error => {
                    console.log(error)
                    this.facilityBlacklist = [];
                }
            )
        },

        saveUser()
        {
            if(this.phoneNumber == null) {
                this.phoneErrorColor = "red"
                this.phoneValidationError = "The phone number is required";
            }
            this.user.facility_id = this.facility.id;

            FacilityBlacklistService.store(this.user).then(
                (response) => {

                    if (response.status === 200) {

                        // this.facilityBlacklist.push(response.data.data);
                        this.$store.dispatch("alert/success", this.user.name +" created");
                        this.loading = false;
                        this.getBlacklist();                        

                    } else {

                        this.$store.dispatch("alert/error", (this.user.name ?? "User") +" not created" + response.message);

                    }

                    this.closeModal();

                },
                (error) => {

                    if (error.response.status === 422) {
                        
                        this.apiErrors = error.response.data.errors

                        console.log(error.response.data.errors)

                        this.$store.dispatch(
                        "alert/error",
                        error.response.data.message
                        );
                        this.loading = false;
                        // this.getData();

                    }

                    this.$store.dispatch("alert/error", error.response.data.message);

                }
            );
        },

        saveUserUpdates()
        {
            this.user.facility_id = this.facility.id;

            FacilityBlacklistService.update(this.user).then(
                (response) => {

                    if (response.status == 200) {

                        // this.facilityBlacklist.push(response.data.data);
                        this.$store.dispatch("alert/success", this.user.name +" updated");
                        this.loading = false;
                        this.getBlacklist();                        

                    } else {

                        this.$store.dispatch("alert/error", (this.user.name ?? "User") +" not updated" + response.message);

                    }

                    this.closeModal();

                },
                (error) => {

                    if (error.response.status == 422) {
                        
                        this.apiErrors = error.response.data.errors

                        console.log(error.response.data.errors)

                        this.$store.dispatch(
                        "alert/error",
                        error.response.data.message
                        );
                        this.loading = false;
                        // this.getData();

                    }

                    this.$store.dispatch("alert/error", error.response.data.message);

                }
            );
        },

        editUser(user) 
        {
            this.user.id = user.id;
            this.user.name = user.attributes.name ?? user.attributes.firstname + " " + user.attributes.lastname;
            this.phoneNumber = user.attributes.phone
            this.user.facility_id = this.facility.id
            this.user.id_number = user.attributes.id_number

            this.editUserModal = true;
            this.showUserModal = true;

        },

        deleteUser(user) 
        {
            this.user.id = user.id
            FacilityBlacklistService.delete(this.user).then(
                (response) => {

                    if (response.status == 200) {

                        this.$store.dispatch("alert/success", this.user.name +" removed");
                        this.loading = false;
                        this.getBlacklist();                        

                    } 


                },
                (error) => {

                    if (error.response.datas != undefined) {
                        
                        this.$store.dispatch("alert/error", error.response.data.message);

                    }

                }
            );
        },

        addUser()
        {
            this.showUserModal = true;
        },

        closeModal() 
        {
            this.user = new Blacklist()
            this.phoneNumber = null;
            this.assignRoleModal = false;
            this.editUserModal = false;
            this.showUserModal = false;
        },

        updatePhoneNumber(data) {
            this.user.phone = data.e164
        },

    }
}
</script>